import React, { SyntheticEvent, useCallback, useState } from 'react'

import styled from '@emotion/styled'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import type {
  CollectionUUID,
  MenuRecipeTileRequestParams,
  Recipe,
  RecipeUUID,
} from '@library/menu-service'

import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { getDietaryClaims } from '../../utils/getRecipeDietaryClaims'
import { getCookingTimeLabel } from '../CookingTimeText'
import { FlexPortionTileEditor } from '../Flex'
import { getRecipeSubtitleItems } from '../RecipeSubtitle'
import { RecipeTileHeader } from '../RecipeTileHeader'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'
import { cssRecipeTile, cssRecipeTileContainer } from './styles'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div(() => ({
  ...cssRecipeTileContainer,
}))

const dietaryLabels: Record<string, string> = {
  vegan: 'Plant based',
  vegetarian: 'Vegetarian',
  dairy_free: 'Dairy free',
  gluten_free: 'Gluten free',
}

const getRecipeTileAriaLabel = ({
  recipe,
  numPortions,
  menuPreferences,
  showCaloriesText,
  showCustomisedTileFooter,
}: {
  recipe: Recipe
  numPortions: number
  menuPreferences?: MenuPreferences
  showCaloriesText: boolean
  showCustomisedTileFooter: boolean
}) => {
  const announcementParts = [recipe.name]

  getDietaryClaims(recipe).forEach((claim) => {
    announcementParts.push(dietaryLabels[claim] ?? claim)
  })

  if (showCustomisedTileFooter) {
    getRecipeSubtitleItems({ recipe, numPortions, menuPreferences }).forEach((item) => {
      announcementParts.push(item.accessibleText)
    })
  } else {
    const cookingTimeLabel = getCookingTimeLabel({ recipe, numPortions })
    if (cookingTimeLabel) {
      announcementParts.push(cookingTimeLabel)
    }

    const caloriesPerPortion = recipe.nutritionalInformation.perPortion.energyKcal
    if (showCaloriesText && caloriesPerPortion) {
      announcementParts.push(`${caloriesPerPortion} calories`)
    }
  }

  return announcementParts.join('. ')
}

export type RecipeTileProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  menuId: string | null
  onClick: (recipeId: RecipeUUID) => void
  menuPreferences?: MenuPreferences
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe: (recipeId: RecipeUUID) => void
  removeRecipe: (recipeId: RecipeUUID) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  selectVariant: ({
    variant,
    variantOutOfStock,
    originalRecipeId,
    recipeReference,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
    originalRecipeId: RecipeUUID
    recipeReference: string
  }) => void
  alternativeOptions: AlternativeOptions
  onOpenAlternativeOptions?: () => void
  isLoadingOptions?: boolean
  recipeReference: string
  originalId: RecipeUUID
  lazy?: boolean
  insideOverflow?: boolean
  index?: number
  quantity?: number
  showCustomisationEducation?: boolean
  closeCustomisationEducation?: () => void
  showCustomisedTileFooter?: boolean
  showFlexPortionTileBadge?: boolean
  numPortions: number
  onUpdateRecipePortions?: (
    recipeId: RecipeUUID,
    numPortions: MenuRecipeTileRequestParams['numPortions'],
  ) => void
}

export const RecipeTile = ({
  recipe,
  currentCollectionId: categoryId,
  menuId,
  onClick,
  menuPreferences,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  selectVariant,
  alternativeOptions,
  isLoadingOptions,
  onOpenAlternativeOptions,
  recipeReference,
  originalId,
  lazy = true,
  insideOverflow = false,
  index,
  quantity = 0,
  showCustomisationEducation = false,
  closeCustomisationEducation = undefined,
  showCustomisedTileFooter = false,
  showFlexPortionTileBadge = false,
  numPortions,
  onUpdateRecipePortions,
}: RecipeTileProps) => {
  const [isFlexPortionEditorOpen, setIsFlexPortionEditorOpen] = useState(false)
  const { id: recipeUuid } = recipe
  const showCaloriesText =
    !menuPreferences || isCaloriePreferenceSelected(menuPreferences.display?.list)
  const showFlexEditor = showFlexPortionTileBadge && isInBasket && quantity > 0

  const handleEditFlexPortions = () => setIsFlexPortionEditorOpen((isOpen) => !isOpen)

  const selectRecipeVariant = useCallback(
    ({ variant, variantOutOfStock }: { variant: Recipe; variantOutOfStock: boolean }) => {
      selectVariant({
        variant,
        variantOutOfStock,
        originalRecipeId: originalId,
        recipeReference,
      })
    },
    [originalId, recipeReference, selectVariant],
  )

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeUuid)
  }

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      aria-label={getRecipeTileAriaLabel({
        recipe,
        numPortions,
        menuPreferences,
        showCaloriesText,
        showCustomisedTileFooter,
      })}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeUuid}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer>
        <RecipeTileHeader
          recipe={recipe}
          currentCollectionId={categoryId}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={showCustomisedTileFooter}
          setBrowserCTAVisible={setBrowserCTAVisible}
          canAddRecipes={canAddRecipes}
          addRecipe={addRecipe}
          removeRecipe={removeRecipe}
          reachedLimit={reachedLimit}
          isInBasket={isInBasket}
          isOutOfStock={isOutOfStock}
          lazy={lazy}
          insideOverflow={insideOverflow}
          index={index}
          showPurchaseInfo={true}
          numPortions={numPortions}
          quantity={quantity}
          showFlexPortionTileBadge={showFlexPortionTileBadge}
          isFlexPortionEditorOpen={isFlexPortionEditorOpen}
          onEditFlexPortions={handleEditFlexPortions}
          alternativeOptions={alternativeOptions}
        />
        {showFlexEditor && (
          <FlexPortionTileEditor
            isOpen={isFlexPortionEditorOpen}
            recipeCount={quantity}
            recipeId={recipe.id}
            numPortions={numPortions}
            portions={recipe.portions}
            onClose={() => setIsFlexPortionEditorOpen(false)}
            onRemoveRecipe={removeRecipe}
            onUpdateRecipePortions={onUpdateRecipePortions}
          />
        )}
        <SwapAlternativeOptions
          currentRecipeId={recipeUuid}
          currentCollectionId={categoryId}
          menuId={menuId}
          menuPreferences={menuPreferences}
          recipe={recipe}
          originalId={originalId}
          reachedLimit={reachedLimit}
          selectVariant={selectRecipeVariant}
          addRecipeToBasket={addRecipe}
          alternativeOptions={alternativeOptions}
          onOpenAlternativeOptions={onOpenAlternativeOptions}
          isLoading={isLoadingOptions || false}
          canAddRecipes={canAddRecipes}
          removeRecipe={removeRecipe}
          quantity={quantity}
          recipeIndex={index}
          showCustomisationEducation={showCustomisationEducation}
          closeCustomisationEducation={closeCustomisationEducation}
        />
      </TileContainer>
    </OuterContainer>
  )
}
